import React from "react";
import { Target, Compass, ShieldCheck, ArrowRight } from "lucide-react";
import { COMPANY, EMAILS } from "@/lib/content";
import { Link } from "react-router-dom";
import EmailStrip from "@/components/EmailStrip";

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <section className="vj-dark vj-radial-fade px-6 lg:px-12 py-20">
        <div className="max-w-[1400px]">
          <div className="overline text-slate-400">About Us</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tighter leading-[1.05] mt-4 max-w-4xl">
            A serious Indian technology enterprise built for global scale.
          </h1>
        </div>
      </section>

      <section className="px-6 lg:px-12 py-16 max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline">01 / Overview</div>
            <h2 className="font-display text-3xl font-extrabold mt-3 tracking-tight">Company Overview</h2>
          </div>
          <div className="lg:col-span-8 text-slate-600 leading-relaxed text-[15px] space-y-4">
            <p>
              VAJRON Global Tech Pvt. Ltd. is an Indian technology enterprise focused on engineering advanced drones, autonomous
              platforms, robotics, artificial intelligence, automation, embedded systems, sensing platforms, and mission-critical
              software for defence organizations, government agencies, industries, and global enterprise customers.
            </p>
            <p>
              Our mandate is to deliver technology that is engineered with discipline, designed for operational seriousness, and
              built for long-term industrial relevance. We approach every product, platform, and capability as a long-horizon
              engineering investment — not a short-cycle product.
            </p>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-12 py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-[1400px] grid lg:grid-cols-2 gap-8">
          <div className="vj-card p-8" data-testid="about-mission-card">
            <Target size={22} className="text-[#1E3A8A]" strokeWidth={1.5}/>
            <div className="overline mt-5">Mission</div>
            <h3 className="font-display text-2xl font-extrabold mt-2 tracking-tight">Engineering advanced systems that nations and enterprises can trust.</h3>
            <p className="text-slate-600 mt-4 leading-relaxed">
              To engineer autonomous, intelligent, and mission-critical technologies of global standard — empowering defence,
              government, and industry to operate with greater capability, safety, and intelligence.
            </p>
          </div>
          <div className="vj-card p-8" data-testid="about-vision-card">
            <Compass size={22} className="text-[#1E3A8A]" strokeWidth={1.5}/>
            <div className="overline mt-5">Vision</div>
            <h3 className="font-display text-2xl font-extrabold mt-2 tracking-tight">To become a globally recognized Indian technology enterprise.</h3>
            <p className="text-slate-600 mt-4 leading-relaxed">
              We are building VAJRON to stand among the world's most respected technology houses — known for engineering depth,
              scale, and the ability to deliver advanced platforms across drones, robotics, AI, and autonomous systems.
            </p>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-12 py-16 max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline">02 / Leadership</div>
            <h2 className="font-display text-3xl font-extrabold mt-3 tracking-tight">Founder</h2>
          </div>
          <div className="lg:col-span-8">
            <div className="vj-card p-8" data-testid="founder-card">
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-emerald-600"/>
                <div className="overline">Founder & CEO</div>
              </div>
              <h3 className="font-display text-3xl font-extrabold tracking-tight mt-3">{COMPANY.founder}</h3>
              <p className="text-slate-600 mt-4 leading-relaxed max-w-3xl">
                VAJRON Global Tech Pvt. Ltd. was founded by {COMPANY.founder} with the vision of building a globally recognized
                Indian technology enterprise focused on advanced drones, autonomous systems, robotics, artificial intelligence,
                automation, and next-generation engineering solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-12 py-16 vj-dark">
        <div className="max-w-[1400px] flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="overline text-slate-400">Engage With Us</div>
            <h3 className="font-display text-2xl md:text-3xl font-extrabold text-white tracking-tight mt-2">Partner with VAJRON on advanced technology programs.</h3>
          </div>
          <div className="flex gap-3">
            <Link to="/solutions" className="vj-btn-ghost-dark" data-testid="about-solutions-link">Explore Solutions</Link>
            <Link to="/contact" className="vj-btn-primary" data-testid="about-contact-link">Contact Us <ArrowRight size={14}/></Link>
          </div>
        </div>
      </section>

      <EmailStrip
        heading="For general information and support, reach out anytime."
        emails={[{ label: "Support & Information", address: EMAILS.support }]}
      />
    </div>
  );
}
